import { generatePath } from 'react-router-dom';

import { ActionButtons } from '@xing-com/crate-jobs-components-job-teaser-options';
import { JDP_JOB_SLUGS } from '@xing-com/crate-jobs-constants';
import type {
  JobRecommendationFragment,
  JobTeaserVisibleJobFragment,
} from '@xing-com/crate-jobs-graphql';
import { selectJobData } from '@xing-com/crate-jobs-helpers';
import { useJobBookmarkToggle } from '@xing-com/crate-jobs-hooks';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import {
  InViewTracking,
  useJobsNwtTracking,
} from '@xing-com/crate-jobs-tracking';
import { JobTeaserCard } from '@xing-com/crate-jobs-xinglets';

type Props = {
  recommendation: JobRecommendationFragment;
  slugList: string[];
  index: number;
};

const params = new URLSearchParams({
  ijt: 'jb_63',
  sc_o: 'jobs_similar_jobs_recommendations',
  sc_o_PropActionOrigin: 'jobs_find_jobs',
}).toString();

export const SimilarJob: React.FC<Props> = ({
  recommendation,
  slugList,
  index,
}) => {
  const nwtTrack = useJobsNwtTracking();
  const { job, trackingToken, position } = recommendation;

  // Already filtered visible jobs in useSimilarJobs hook
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const visibleJob = job as JobTeaserVisibleJobFragment;
  const { isBookmarked } = selectJobData(visibleJob);

  const path = generatePath(ROUTES.jobDetails, {
    slug: visibleJob.slug,
  });
  const url = `${path}?${params}`;
  const routerState = {
    [JDP_JOB_SLUGS]: {
      list: slugList,
      type: 'recos',
      index: recommendation.position,
    },
  };
  const nwtTrackingOptions = {
    element: 'job_posting',
    elementDetail: 'job_recommendation',
    sentBy: 'welcome',
    eventSchema: 'extended',
    itemUrn: visibleJob.globalId,
    page: 'welcome/start/index',
    position_in_module: position,
    module_position: index,
    module_title: 'jobs_recommendation',
    trackingToken,
  } as const;

  const onBookmark = useJobBookmarkToggle({
    jobId: visibleJob.id,
    isBookmarked,
    nwtTrackingOptions,
  });

  const handleOnClick = (): void => {
    nwtTrack({ ...nwtTrackingOptions, event: 'opened' });
  };

  return (
    <InViewTracking
      nwtTrackingOptions={{ ...nwtTrackingOptions, event: 'entered_viewport' }}
    >
      <JobTeaserCard
        job={{
          ...visibleJob,
          activatedAt: null,
        }}
        url={url}
        routerState={routerState}
        onClick={handleOnClick}
        onContextMenu={handleOnClick}
        onAuxClick={handleOnClick}
        headingLevel="h3"
        profileImageSize="medium"
        profileImageSizeConfined="medium"
        data-testid="similar-job-recommendation-card"
        actions={
          <ActionButtons
            iconsOnly
            size="medium"
            isBookmarked={isBookmarked}
            onBookmark={onBookmark}
            bottom={false}
            top
            noMargin
          />
        }
      />
    </InViewTracking>
  );
};
